// Rolling-Sharpe momentum signal, ported from the research PineScript
// "Rolling Sharpe (Regime + 200D SMA Filter) — No Reaccel/Logit".
//
// Definition:
// - per-candle simple or log returns, minus the per-period risk-free rate
// - fast Sharpe: rolling mean/stdev of excess returns over `win`, annualized,
//   then smoothed with an SMA of `fastSharpeSMA`
// - slow Sharpe: same over a dynamic window `win2 / clamp(sqrt(realVol / baseVol), 1, 2)`
//   (realized vol shrinks the slow window by up to 2x), clamped to [minWinCap, maxWinCap]
// - bull when slow > thresh and fast > shorthresh; bear when both clear the
//   negated thresholds
// - optional regime gate: the same bull/bear computed on another instrument's
//   candles (e.g. BTC momentum gating an altcoin trade)
// - optional long-SMA filter: price above an SMA (default 200 x 1d) on a
//   configurable symbol/timeframe, with a crossunder exit at `maRatio` of the SMA
// - entry when regime bullish AND above SMA AND chart bullish; exit when chart
//   bearish OR regime bearish OR crossunder
//
// The signal is timeframe-agnostic: it runs on whatever interval is charted and
// annualizes with `periodsPerYear` (0 = derived from the interval and asset class).
import type { Candle } from './types';

export interface MomentumParams {
  useLogReturns: boolean;
  win: number;
  win2: number;
  periodsPerYear: number;
  rfAnnual: number;
  baseAnnVol: number;
  volLookback: number;
  minWinCap: number;
  maxWinCap: number;
  thresh: number;
  shorthresh: number;
  fastSharpeSMA: number;
  slowSharpeSMA: number;
  useRegime: boolean;
  regimeSymbol: string;
  use200: boolean;
  maSymbol: string;
  maInterval: string;
  maLen: number;
  maRatio: number;
}

export const defaultMomentumParams: MomentumParams = {
  useLogReturns: false,
  win: 63,
  win2: 63,
  periodsPerYear: 0,
  rfAnnual: 0,
  baseAnnVol: 0.16,
  volLookback: 63,
  minWinCap: 10,
  maxWinCap: 252,
  thresh: 0,
  shorthresh: 0,
  fastSharpeSMA: 5,
  slowSharpeSMA: 21,
  useRegime: false,
  regimeSymbol: '',
  use200: false,
  maSymbol: '',
  maInterval: '1d',
  maLen: 200,
  maRatio: 0.995,
};

const EPSILON = 1e-12;

export function momentumParams(raw: Record<string, unknown>): MomentumParams {
  const out = { ...defaultMomentumParams } as Record<string, unknown>;
  for (const [key, fallback] of Object.entries(defaultMomentumParams)) {
    const value = raw[key];
    if (typeof fallback === 'boolean') out[key] = value === undefined ? fallback : Boolean(value);
    else if (typeof fallback === 'number')
      out[key] = Number.isFinite(Number(value)) && value !== '' && value !== undefined ? Number(value) : fallback;
    else out[key] = typeof value === 'string' ? value.trim() : fallback;
  }
  return out as unknown as MomentumParams;
}

export function momentumParamsValid(raw: Record<string, unknown>): boolean {
  const p = momentumParams(raw);
  return (
    p.win >= 2 &&
    p.win2 >= 2 &&
    p.volLookback >= 2 &&
    p.minWinCap >= 2 &&
    p.maxWinCap >= p.minWinCap &&
    p.fastSharpeSMA >= 1 &&
    p.slowSharpeSMA >= 1 &&
    p.maLen >= 2 &&
    p.maRatio > 0.5 &&
    p.maRatio < 1.5 &&
    p.periodsPerYear >= 0
  );
}

const INTERVAL_MINUTES: Record<string, number> = { '1m': 1, '5m': 5, '15m': 15, '1h': 60, '4h': 240, '1d': 1440 };

export function intervalMinutes(interval: string): number {
  return INTERVAL_MINUTES[interval] ?? 1;
}

export function autoPeriodsPerYear(interval: string, assetClass: string | null): number {
  const minutes = intervalMinutes(interval);
  const session = assetClass === 'equity' || assetClass === 'index';
  if (minutes >= 1440) return session ? 252 : 365;
  return (session ? 252 * 390 : 365 * 1440) / minutes;
}

/** Rolling statistic with a (possibly per-bar) window. NaN inputs poison their windows. */
function rolling(values: number[], windowAt: (index: number) => number, statistic: 'mean' | 'std'): number[] {
  return values.map((_, index) => {
    const window = Math.max(1, Math.round(windowAt(index)));
    if (index + 1 < window) return NaN;
    let sum = 0;
    for (let j = index - window + 1; j <= index; j += 1) sum += values[j];
    if (!Number.isFinite(sum)) return NaN;
    const mean = sum / window;
    if (statistic === 'mean') return mean;
    let squares = 0;
    for (let j = index - window + 1; j <= index; j += 1) squares += (values[j] - mean) ** 2;
    return Math.sqrt(squares / window);
  });
}

export interface SharpeSignals {
  sharpeFast: number[];
  sharpeSlow: number[];
  sharpeSlowSMA: number[];
  bull: boolean[];
  bear: boolean[];
  slowWindow: number[];
}

export function calcSignals(closes: number[], p: MomentumParams, periodsPerYear: number): SharpeSignals {
  const rfPer = p.rfAnnual / periodsPerYear;
  const ret = closes.map((close, index) =>
    index === 0 ? NaN : p.useLogReturns ? Math.log(close / closes[index - 1]) : close / closes[index - 1] - 1,
  );
  const excess = ret.map((value) => value - rfPer);
  const retStd = rolling(ret, () => p.volLookback, 'std');
  const slowWindow = retStd.map((sd) => {
    const realized = Number.isFinite(sd) ? sd * Math.sqrt(periodsPerYear) : p.baseAnnVol;
    const scale = Math.min(Math.max(Math.sqrt(Math.max(realized, EPSILON) / Math.max(p.baseAnnVol, EPSILON)), 1), 2);
    return Math.round(Math.min(p.maxWinCap, Math.max(p.minWinCap, p.win2 / scale)));
  });
  const annualize = Math.sqrt(periodsPerYear);
  const meanFast = rolling(excess, () => p.win, 'mean');
  const stdFast = rolling(excess, () => p.win, 'std');
  const meanSlow = rolling(excess, (index) => slowWindow[index], 'mean');
  const stdSlow = rolling(excess, (index) => slowWindow[index], 'std');
  const fastRaw = meanFast.map((mean, index) => (stdFast[index] > 0 ? (mean / stdFast[index]) * annualize : NaN));
  const sharpeSlow = meanSlow.map((mean, index) => (stdSlow[index] > 0 ? (mean / stdSlow[index]) * annualize : NaN));
  const sharpeFast = rolling(fastRaw, () => p.fastSharpeSMA, 'mean');
  const sharpeSlowSMA = rolling(sharpeSlow, () => p.slowSharpeSMA, 'mean');
  const bull = sharpeSlow.map((slow, index) => slow > p.thresh && sharpeFast[index] > p.shorthresh);
  const bear = sharpeSlow.map((slow, index) => slow < -p.thresh && sharpeFast[index] < -p.shorthresh);
  return { sharpeFast, sharpeSlow, sharpeSlowSMA, bull, bear, slowWindow };
}

/**
 * Long-SMA filter mapped onto chart bars without lookahead: each chart bar sees
 * only filter bars that have already closed (open + interval <= chart bar open).
 */
export function maFilter(
  chartOpenMs: number[],
  filterCandles: Candle[],
  p: MomentumParams,
): { aboveMA: boolean[]; crossUnder: boolean[] } {
  const closes = filterCandles.map((candle) => Number(candle.close));
  const ma = rolling(closes, () => p.maLen, 'mean');
  const above = closes.map((close, index) => close > ma[index]);
  const crossUnderBar = closes.map((close, index) => {
    if (index === 0 || !Number.isFinite(ma[index]) || !Number.isFinite(ma[index - 1])) return false;
    return close < ma[index] * p.maRatio && closes[index - 1] >= ma[index - 1] * p.maRatio;
  });
  const filterMs = intervalMinutes(p.maInterval) * 60_000;
  const closeMs = filterCandles.map((candle) => Date.parse(candle.time) + filterMs);
  let cursor = -1;
  let previousCursor = -1;
  const aboveMA: boolean[] = [];
  const crossUnder: boolean[] = [];
  for (const openMs of chartOpenMs) {
    while (cursor + 1 < closeMs.length && closeMs[cursor + 1] <= openMs) cursor += 1;
    aboveMA.push(cursor >= 0 && above[cursor] === true);
    let crossed = false;
    for (let j = Math.max(previousCursor + 1, 1); j <= cursor; j += 1) crossed = crossed || crossUnderBar[j];
    crossUnder.push(crossed);
    previousCursor = cursor;
  }
  return { aboveMA, crossUnder };
}

/** Map regime-series signals (same interval) onto chart bars by forward-filling the latest regime bar. */
function mapRegime(
  chartOpenMs: number[],
  regimeCandles: Candle[],
  signals: SharpeSignals,
): { bull: boolean[]; bear: boolean[]; fast: number[]; slow: number[] } {
  const regimeMs = regimeCandles.map((candle) => Date.parse(candle.time));
  let cursor = -1;
  const bull: boolean[] = [];
  const bear: boolean[] = [];
  const fast: number[] = [];
  const slow: number[] = [];
  for (const openMs of chartOpenMs) {
    while (cursor + 1 < regimeMs.length && regimeMs[cursor + 1] <= openMs) cursor += 1;
    bull.push(cursor >= 0 && signals.bull[cursor]);
    bear.push(cursor >= 0 && signals.bear[cursor]);
    fast.push(cursor >= 0 ? signals.sharpeFast[cursor] : NaN);
    slow.push(cursor >= 0 ? signals.sharpeSlow[cursor] : NaN);
  }
  return { bull, bear, fast, slow };
}

export interface MomentumLine {
  label: string;
  color: string;
  width: 1 | 2;
  points: Array<{ time: string; value: number }>;
}
export interface MomentumMarker {
  time: string;
  kind: 'enter' | 'exit';
  label: string;
}
export interface MomentumComputation {
  lines: MomentumLine[];
  markers: MomentumMarker[];
  warnings: string[];
}

export interface MomentumContext {
  interval: string;
  assetClass: string | null;
  regimeCandles?: Candle[];
  filterCandles?: Candle[];
}

export function computeMomentum(
  candles: Candle[],
  rawParams: Record<string, unknown>,
  context: MomentumContext,
): MomentumComputation {
  const p = momentumParams(rawParams);
  const warnings: string[] = [];
  if (candles.length < 2) return { lines: [], markers: [], warnings };
  const periodsPerYear =
    p.periodsPerYear > 0 ? p.periodsPerYear : autoPeriodsPerYear(context.interval, context.assetClass);
  const chartOpenMs = candles.map((candle) => Date.parse(candle.time));
  const chart = calcSignals(
    candles.map((candle) => Number(candle.close)),
    p,
    periodsPerYear,
  );

  let bullReg: boolean[] = candles.map(() => true);
  let bearReg: boolean[] = candles.map(() => false);
  let regime: { fast: number[]; slow: number[] } | null = null;
  if (p.useRegime && p.regimeSymbol) {
    if (context.regimeCandles && context.regimeCandles.length > 1) {
      const signals = calcSignals(
        context.regimeCandles.map((candle) => Number(candle.close)),
        p,
        periodsPerYear,
      );
      const mapped = mapRegime(chartOpenMs, context.regimeCandles, signals);
      bullReg = mapped.bull;
      bearReg = mapped.bear;
      regime = { fast: mapped.fast, slow: mapped.slow };
    } else {
      warnings.push(`regime candles unavailable for ${p.regimeSymbol}; regime gate skipped`);
    }
  } else if (p.useRegime) {
    warnings.push('regime gate enabled without a regime symbol; gate skipped');
  }

  let aboveMA: boolean[] = candles.map(() => true);
  let crossUnder: boolean[] = candles.map(() => false);
  if (p.use200) {
    if (context.filterCandles && context.filterCandles.length > 0) {
      const filter = maFilter(chartOpenMs, context.filterCandles, p);
      aboveMA = filter.aboveMA;
      crossUnder = filter.crossUnder;
    } else {
      warnings.push(
        `filter candles unavailable (${p.maSymbol || 'chart symbol'} @ ${p.maInterval}); SMA filter skipped`,
      );
    }
  }

  const markers: MomentumMarker[] = [];
  let long = false;
  for (let index = 0; index < candles.length; index += 1) {
    if (!long && bullReg[index] && aboveMA[index] && chart.bull[index]) {
      long = true;
      markers.push({ time: candles[index].time, kind: 'enter', label: 'Enter' });
    } else if (long && (chart.bear[index] || bearReg[index] || crossUnder[index])) {
      long = false;
      const reason = chart.bear[index] ? 'chart' : bearReg[index] ? 'regime' : 'SMA';
      markers.push({ time: candles[index].time, kind: 'exit', label: `Exit (${reason})` });
    }
  }

  const points = (values: number[]) =>
    values
      .map((value, index) => ({ time: candles[index].time, value }))
      .filter((point) => Number.isFinite(point.value));
  const lines: MomentumLine[] = [
    { label: 'Sharpe fast', color: '#f23645', width: 2, points: points(chart.sharpeFast) },
    { label: 'Sharpe slow', color: '#f4b740', width: 2, points: points(chart.sharpeSlow) },
    { label: 'Sharpe slow SMA', color: '#22ab94', width: 2, points: points(chart.sharpeSlowSMA) },
  ];
  if (regime) {
    lines.push({ label: 'Regime fast', color: '#b388ff', width: 1, points: points(regime.fast) });
    lines.push({ label: 'Regime slow', color: '#39b9ff', width: 1, points: points(regime.slow) });
  }
  return { lines, markers, warnings };
}
